import { SidePanelContent } from "@components/sidepanel/sidepanel-content";
import { BaseMarkupProps } from "@custom-types/project-markups-types";
import { MarkupDueDate } from "@pages/project-details/project-markups/due-date/markup-due-date";
import { MarkupAssignee } from "@pages/project-details/project-markups/markup-assignee";
import { MarkupDescription } from "@pages/project-details/project-markups/sidepanel/markup-description";
import { MarkupSidePanelTags } from "@pages/project-details/project-markups/sidepanel/markup-sidepanel-tags";
import { MarkupStatusDropdown } from "@pages/project-details/project-markups/status/markup-status-dropdown";
import { MarkupMeasurements } from "@pages/project-details/project-markups/sidepanel/markup-measurements";
import { useAppSelector } from "@store/store-helper";
import { convertDistance } from "@utils/unit-utils";
import { userSettingsSelector } from "@store/settings/settings-selector";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { getCoordinateZUp } from "@utils/vector-utils";

/**
 * This component returns each item that will be inserted in the side panel
 */
export function MarkupsSidePanelInfo({ markup }: BaseMarkupProps): JSX.Element {
  const userSettings = useAppSelector(userSettingsSelector);
  const unitMeasurement: SettingsApiTypes.UnitMeasurement =
    userSettings.unit_measurement?.value ?? "meters";
  let convertedGlobalPose = null;

  /**
   * Get the markup global pose in the same coordinate system as
   * displayed in the Sphere Viewer and Scene app
   */
  const markupPoseZUp = markup.globalPose
    ? getCoordinateZUp(markup.globalPose.pos)
    : null;

  if (markupPoseZUp) {
    // Convert the global pose from the default unit to the user selected unit of measurement
    const convertedX = convertDistance(markupPoseZUp.x, unitMeasurement);
    const convertedY = convertDistance(markupPoseZUp.y, unitMeasurement);
    const convertedZ = convertDistance(markupPoseZUp.z, unitMeasurement);
    convertedGlobalPose = `X: ${convertedX}; Y: ${convertedY}; Z: ${convertedZ}`;
  }

  return (
    <>
      <SidePanelContent
        items={[
          {
            title: "Sheet",
            value: <var>{markup?.areaSection?.name || "-"}</var>,
          },
          { title: "Scene", value: <var>{markup?.section?.name || "-"}</var> },
          {
            title: "Assignee",
            value: <MarkupAssignee markup={markup} isSidePanelOpen={true} />,
          },
          {
            title: "Status",
            value: (
              <MarkupStatusDropdown markup={markup} isSidePanelOpen={true} />
            ),
          },
          {
            title: "Due Date",
            value: <MarkupDueDate markup={markup} isSidePanelOpen={true} />,
          },
          {
            title: "Global Pose",
            value: convertedGlobalPose ? <var>{convertedGlobalPose}</var> : "-",
          },
        ]}
      />

      {markup && <MarkupSidePanelTags labels={markup.labels} />}

      {markup && <MarkupDescription markup={markup} />}

      {markup.measurePolygon && (
        <MarkupMeasurements
          measurePolygon={markup.measurePolygon}
          unitMeasurement={unitMeasurement}
        />
      )}
    </>
  );
}
